/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */

import { TileInstitutionData } from '@/api/dtos';
import { ITrial } from '@/components/TrialsArea';
import { Divider, List } from '@mui/material';
import * as React from 'react';
import { Fragment } from 'react';
import { StyledList } from '.';
import TileInformationData from './InformationItem/TileInformationData';
import ExpandableMenuItem from './MenuItem/ExpandableMenuItem';
import MenuItemContents from './MenuItem/MenuItemContents';
import RecursiveMenuItem from './MenuItem/RecursiveMenuItem';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

interface IListOfContents {
    tileInstitution: TileInstitutionData | undefined
    tileData?: Record<string, string>;
    trial: ITrial;
    expandedMenuItemsNonFav?: Record<number, boolean> | undefined;
    toggleExpandedMenuItem?: (trialId: number, expandMenuItemId: number) => void | undefined;
    toggleExpandedMenuItemNonfav?: (expandMenuItemId: number) => void | undefined;
}

const ListOfContents: React.FunctionComponent<IListOfContents> = ({
    tileInstitution,
    tileData,
    trial,
    expandedMenuItemsNonFav,
    toggleExpandedMenuItem,
    toggleExpandedMenuItemNonfav
}) => {


    return (
        <List
            disablePadding
        >
            {
                tileInstitution?.informationItems?.map((informationItem, i) => {
                    return <Fragment key={i}>
                        <TileInformationData 
                            informationItem={informationItem} 
                            trial={trial} 
                            toggleExpandedMenuItem={toggleExpandedMenuItem}
                            toggleExpandedMenuItemNonfav={toggleExpandedMenuItemNonfav}
                            expandedMenuItemsNonFav={expandedMenuItemsNonFav}
                        />
                        <Divider />
                    </Fragment>
                })
            }

            {
                tileInstitution?.tileMenuItems?.map((menuItem, i) => {
                    return (
                        <Fragment key={i}>
                            {
                                !!menuItem.expandable ? (
                                    <>
                                        <ExpandableMenuItem
                                            tileMenuItem={menuItem}
                                            tileData={tileData}
                                            toggleExpandedMenuItem={toggleExpandedMenuItem}
                                            toggleExpandedMenuItemNonfav={toggleExpandedMenuItemNonfav}
                                            expandedMenuItemsNonFav={expandedMenuItemsNonFav}
                                            trial={trial}
                                        />
                                            
                                        <Divider />
                                    </>
                                ) : (
                                    <>
    
                                        <MenuItemContents 
                                            menuItem={menuItem}
                                            tileData={tileData}
                                        />
                                        
                                        <Divider />
                                        
                                        {
                                            menuItem.subTileMenuItems !== null && menuItem.subTileMenuItems !== undefined &&
                                            <>
                                                <StyledList
                                                    disablePadding
                                                >
                                                    {
                                                        menuItem.subTileMenuItems.map((menuItem, j, entireList) => (
            
                                                            <RecursiveMenuItem 
                                                                key={`${i}-${j}`} 
                                                                menuItem={menuItem} 
                                                                finalInList={(j === entireList.length-1)} 
                                                                finalComponent={false} 
                                                                tileData={tileData}
                                                                toggleExpandedMenuItem={toggleExpandedMenuItem}
                                                                toggleExpandedMenuItemNonfav={toggleExpandedMenuItemNonfav}
                                                                expandedMenuItemsNonFav={expandedMenuItemsNonFav}
                                                                trial={trial}
                                                            />
            
                                                        ))
                                                    }
                                                </StyledList>
                                                <Divider />
                                            </>
                                        }
                                    </>
                                )
                            }
                        </Fragment>
                    )
                })
            }

        </List>
        
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default ListOfContents;